import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import HeroBadge from "@/components/HeroBadge";
import api from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { fetchPlans, FALLBACK_PLANS } from "@/lib/plans";
import { inr, inrFull } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft, Lock, ShieldCheck, BadgePercent, Info } from "lucide-react";
import { RelationSelect, validateFamilyComposition } from "@/lib/relations";
import { ageLabel } from "@/lib/age";

const STEPS = ["Plan", "Applicant", "Family", "Documents", "Discount Code", "Review", "Pay"];

// Indian states (alphabetised) used for GST place-of-supply selection.
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export default function JoinNPC() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);   // post-payment overlay
  const [refStatus, setRefStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({
    plan_id: sp.get("plan") || "",
    full_name: "", mobile: "", email: "", dob: "", gender: "",
    address: "", aadhaar: "",
    gst_number: "",
    state: "",
    city: "",
    pincode: "",
    family_members_count: 0,
    family_member_names: [],
    family_members: [],
    documents: { aadhaar: null, photo: null, aadhaar_uploaded: false, photo_uploaded: false },
    referral_code: (sp.get("ref") || "").trim().toUpperCase(),
    terms_accepted: false,
  });
  // Realtime duplicate-check state: { field -> {duplicate, message} }
  const [dupErrors, setDupErrors] = useState({});

  // While the post-payment overlay is up, prevent accidental refresh/close — the
  // browser will show its native "Leave site?" prompt. We tear this down as soon
  // as the navigate(/success) fires (finalizing → false in the `finally`).
  useEffect(() => {
    if (!finalizing) return;
    const block = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", block);
    return () => window.removeEventListener("beforeunload", block);
  }, [finalizing]);

  useEffect(() => {
    let ok = true;
    fetchPlans().then((list) => {
      if (!ok) return;
      setPlans(list);
      if (!form.plan_id && list[0]) setForm((f) => ({ ...f, plan_id: list[0].plan_id }));
    });
    return () => { ok = false; };
    // eslint-disable-next-line
  }, []);

  const plan = plans.find((p) => p.plan_id === form.plan_id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live pricing preview whenever plan or applied referral changes
  useEffect(() => {
    if (!form.plan_id) { setPricing(null); return; }
    const code = refStatus?.valid ? form.referral_code : "";
    api.get(`/pricing/preview?plan_id=${form.plan_id}${code ? `&referral_code=${encodeURIComponent(code)}` : ""}`)
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, [form.plan_id, refStatus, form.referral_code]);

  // Step-wise required-field validation. Returns null when the step is valid, else an error message.
  const validateStep = (s) => {
    if (s === 0 && !form.plan_id) return "Please select a plan to continue.";
    if (s === 1) {
      if (!form.full_name.trim()) return "Full name is required.";
      if (!/^\d{10}$/.test(form.mobile)) return "Enter a valid 10-digit mobile number.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Enter a valid email address.";
      if (!form.dob) return "Date of birth is required.";
      if (!form.gender) return "Please select a gender.";
      if (!/^\d{12}$/.test(form.aadhaar)) return "Aadhaar must be exactly 12 digits.";
      if (!form.address.trim()) return "Address is required.";
      if (!form.city.trim()) return "City is required.";
      if (!/^\d{6}$/.test(form.pincode)) return "Pincode must be exactly 6 digits.";
      if (!form.state) return "Please select a state.";
      const blockedField = ["email", "mobile", "aadhaar", "dob"].find((k) => dupErrors[k]?.duplicate);
      if (blockedField) return dupErrors[blockedField].message;
    }
    if (s === 2 && plan && plan.tier !== "individual") {
      for (let i = 0; i < form.family_members.length; i++) {
        const m = form.family_members[i];
        if (!m.full_name?.trim()) return `Family member ${i + 2}: full name is required.`;
        if (!m.relation) return `Family member ${i + 2}: please pick a relation.`;
        // Mobile is OPTIONAL for non-primary family members. Validate format only when supplied.
        if (m.mobile && !/^\d{10}$/.test(m.mobile)) return `Family member ${i + 2}: mobile must be 10 digits.`;
        if (!m.dob) return `Family member ${i + 2}: date of birth is required.`;
        if (!/^\d{12}$/.test(m.aadhaar || "")) return `Family member ${i + 2}: Aadhaar must be exactly 12 digits.`;
      }
      const compErr = validateFamilyComposition(plan.tier, form.family_members);
      if (compErr) return compErr;
    }
    if (s === 3) {
      // Aadhaar + photo are now OPTIONAL — can be uploaded later from dashboard.
      return null;
    }
    return null;
  };

  // Realtime duplicate check on blur (email / mobile / aadhaar / dob)
  const runDupCheck = async (field) => {
    const payload = {
      email: field === "email" ? form.email : "",
      mobile: field === "mobile" ? form.mobile : "",
      aadhaar: field === "aadhaar" ? form.aadhaar : "",
      dob: field === "dob" ? form.dob : "",
    };
    // Skip empty / incomplete values
    if (field === "mobile" && !/^\d{10}$/.test(form.mobile)) return;
    if (field === "aadhaar" && !/^\d{12}$/.test(form.aadhaar)) return;
    if (field === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return;
    if (field === "dob" && !form.dob) return;
    try {
      const r = await api.post("/check-duplicate", payload);
      setDupErrors((prev) => ({ ...prev, [field]: r.data }));
      if (r.data.duplicate) toast.error(r.data.message);
    } catch (e) {
      // network blip — ignore; final purchase API will catch it
      console.error("join dup-check failed", e);
    }
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const verifyReferral = async () => {
    if (!form.referral_code) return;
    try {
      const r = await api.get(`/referrals/verify/${encodeURIComponent(form.referral_code)}`);
      setRefStatus(r.data);
      if (r.data.valid) toast.success(`Referral applied · ${r.data.owner}`);
      else toast.error("Discount code not found");
    } catch (e) {
      console.error("referral verify failed", e);
      setRefStatus({ valid: false });
      toast.error("Could not verify code");
    }
  };

  const addFamilyMember = () => {
    const maxExtra = Math.max(0, (plan?.family_members ?? 6) - 1);
    if ((form.family_members?.length || 0) >= maxExtra) {
      toast.error(`This plan allows up to ${maxExtra} additional member${maxExtra === 1 ? "" : "s"}.`);
      return;
    }
    const next = [...form.family_members, { full_name: "", mobile: "", dob: "", relation: "", aadhaar: "" }];
    set("family_members", next);
    set("family_members_count", next.length);
    set("family_member_names", next.map((m) => m.full_name || ""));
  };

  const removeFamilyMember = (idx) => {
    const next = form.family_members.filter((_, i) => i !== idx);
    set("family_members", next);
    set("family_members_count", next.length);
    set("family_member_names", next.map((m) => m.full_name || ""));
  };

  const updFamilyMember = (i, patch) => {
    const arr = [...form.family_members];
    arr[i] = { ...(arr[i] || {}), ...patch };
    set("family_members", arr);
    // keep legacy names in sync
    const names = arr.map((m) => m.full_name || "");
    set("family_member_names", names);
    set("family_members_count", arr.length);
  };

  const submitPay = async () => {
    if (!plan) return;
    if (!form.terms_accepted) {
      toast.error("Please accept the Terms & Conditions to continue.");
      return;
    }
    setLoading(true);
    try {
      const purchase = await api.post("/npc/purchase", {
        plan_id: plan.plan_id,
        full_name: form.full_name, mobile: form.mobile, email: form.email,
        address: form.address, dob: form.dob,
        family_members_count: form.family_members_count,
        family_member_names: form.family_member_names,
        family_members: form.family_members,
        gst_number: form.gst_number || null,
        state: form.state || "",
        city: form.city || "",
        pincode: form.pincode || "",
        referral_code: form.referral_code || null,
        aadhaar: form.aadhaar, gender: form.gender,
        aadhaar_doc_id: form.documents.aadhaar?.doc_id || null,
        photo_doc_id: form.documents.photo?.doc_id || null,
        terms_accepted: form.terms_accepted,
      });
      const order = purchase.data;
      // Real Razorpay flow when backend reports mock:false. Else stay on the
      // dev-only mock path so local testing doesn't require keys.
      let paymentResult;
      if (!order.mock) {
        paymentResult = await openRazorpayCheckout({
          key: order.razorpay_key,
          amount_paise: order.amount_paise,
          order_id: order.order_id,
          name: "Nirmal Privilege Card",
          description: `${plan.name} membership`,
          prefill: { name: form.full_name, email: form.email, contact: form.mobile },
          notes: { plan_id: plan.plan_id },
        });
      } else {
        await new Promise((r) => setTimeout(r, 800));
        paymentResult = {
          order_id: order.order_id,
          payment_id: `pay_mock_${Date.now()}`,
          signature: `sig_mock_${Date.now()}`,
        };
      }
      // Razorpay returned a successful payment — show the full-screen processing
      // overlay IMMEDIATELY so there's no confusing gap while we call
      // /verify-payment (which can take 2–4s for invoice gen + email send).
      setFinalizing(true);
      const verify = await api.post("/memberships/verify-payment", paymentResult);
      if (verify.data.auto_account) {
        sessionStorage.setItem(`npc_creds_${verify.data.membership.member_id}`, JSON.stringify(verify.data.auto_account));
        if (verify.data.auto_account.token) {
          localStorage.setItem("vip_token", verify.data.auto_account.token);
        }
      }
      toast.success("Payment successful · Membership activated");
      // Give the user a beat to read the overlay copy (and lets card/email tasks settle)
      await new Promise((r) => setTimeout(r, 1200));
      navigate(`/success/${verify.data.membership.member_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally { setLoading(false); setFinalizing(false); }
  };

  return (
    <SiteLayout>
      {finalizing && (
        <div className="fixed inset-0 z-[60] bg-[#0F2540]/95 backdrop-blur-sm grid place-items-center" data-testid="payment-finalizing-overlay" style={{ pointerEvents: "auto" }}>
          <div className="text-center px-6 max-w-md">
            {/* Concentric success ring + spinner ring */}
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <div className="absolute inset-0 rounded-full bg-[#1F7A3D]/20 animate-ping" />
              <div className="absolute inset-2 rounded-full border-4 border-white/20 border-t-[#C89B3C] animate-spin" />
              <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#1F7A3D] to-[#155F2D] grid place-items-center shadow-lg">
                <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <h2 className="mt-7 font-display text-2xl sm:text-3xl font-bold text-white">Payment received <span className="block text-[#C89B3C]">successfully</span></h2>
            <p className="mt-3 text-sm sm:text-base text-white/85 leading-relaxed">Please wait while we generate your membership and invoice. Do not refresh or close this window.</p>
            <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-white/60 uppercase tracking-wider font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C89B3C] animate-pulse" />
              <span>Generating digital card · Sending invoice email</span>
            </div>
          </div>
        </div>
      )}
      <section className="mx-auto max-w-5xl px-6 lg:px-10 py-12">
        <HeroBadge>Join NPC · <span className="text-[#A53A2A]">Step {step + 1} of {STEPS.length}</span></HeroBadge>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-[#0F2540]">{STEPS[step]}</h1>

        {/* Stepper */}
        <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap ${i <= step ? "bg-[#1E4F87] border-[#1E4F87] text-white" : "bg-white border-[#E1E8F0] text-[#5C6B7A]"}`} data-testid={`step-${i}`}>
              {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-bold">{i + 1}</span>} {s}
            </div>
          ))}
        </div>

        <div className="mt-8 lux-card rounded-3xl p-6 lg:p-8" data-testid="join-form">
          {step === 0 && (
            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((p) => (
                <button key={p.plan_id} onClick={() => set("plan_id", p.plan_id)}
                  className={`text-left rounded-2xl p-6 border-2 transition ${form.plan_id === p.plan_id ? "border-[#1E4F87] shadow-lg" : "border-[#E1E8F0] hover:border-[#1E4F87]/40"}`}
                  data-testid={`pick-${p.tier}`}>
                  <div className="text-xs uppercase tracking-wider text-[#A53A2A] font-semibold">{p.tier.replace("_", " ")}</div>
                  <div className="mt-1 font-display text-2xl font-bold text-[#0F2540]">{p.name}</div>
                  <div className="mt-1 text-sm text-[#5C6B7A]">{p.tagline}</div>
                  <div className="mt-4 font-display text-3xl font-bold text-[#0F2540]">{inr(p.price)}<span className="text-sm text-[#5C6B7A] font-normal">/yr</span></div>
                  <div className="text-xs text-[#5C6B7A] mt-1">Price includes {p.gst_percent ?? 18}% GST</div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Full Name" v={form.full_name} s={(v) => set("full_name", v)} required tid="j-name" />
              <F label="Mobile" v={form.mobile} s={(v) => set("mobile", v.replace(/\D/g, "").slice(0, 10))} onBlur={() => runDupCheck("mobile")} err={dupErrors.mobile?.duplicate ? dupErrors.mobile.message : ""} required tid="j-mobile" />
              <F label="Email" type="email" v={form.email} s={(v) => set("email", v)} onBlur={() => runDupCheck("email")} err={dupErrors.email?.duplicate ? dupErrors.email.message : ""} required tid="j-email" />
              <F label="Date of Birth" type="date" v={form.dob} s={(v) => set("dob", v)} required tid="j-dob" />
              <div>
                <label className="text-sm font-medium text-[#0F2540]">Gender *</label>
                <select required value={form.gender} onChange={(e) => set("gender", e.target.value)}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="j-gender">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <F label="Aadhaar (12-digit)" v={form.aadhaar} s={(v) => set("aadhaar", v.replace(/\D/g, "").slice(0, 12))} onBlur={() => runDupCheck("aadhaar")} err={dupErrors.aadhaar?.duplicate ? dupErrors.aadhaar.message : ""} required tid="j-aadhaar" />
              <div className="sm:col-span-2"><F label="Address" v={form.address} s={(v) => set("address", v)} required tid="j-address" /></div>
              <F label="City" v={form.city} s={(v) => set("city", v)} required tid="j-city" />
              <F label="Pincode (6-digit)" v={form.pincode} s={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} required tid="j-pincode" />
              <div>
                <label className="text-sm font-medium text-[#0F2540]">State *</label>
                <select required value={form.state} onChange={(e) => set("state", e.target.value)}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="j-state">
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="text-xs text-[#5C6B7A] mt-1">Used for GST place-of-supply (CGST + SGST within Gujarat; IGST otherwise).</p>
              </div>
              <div>
                <F label="Company GST Number (optional — for business invoice)" v={form.gst_number} s={(v) => set("gst_number", v.toUpperCase().slice(0, 15))} tid="j-gst" />
                <p className="text-xs text-[#5C6B7A] mt-1">Provide your company's GSTIN only if you need a B2B invoice. Leave blank otherwise.</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              {plan && plan.tier === "individual" ? (
                <p className="text-[#5C6B7A]">Individual plan — no family members required. Click <span className="font-semibold text-[#0F2540]">Next</span> to continue.</p>
              ) : (
                <>
                  <div className="mb-4 rounded-xl border border-[#1E4F87]/20 bg-[#EAF1FA] px-4 py-3 text-sm text-[#0F2540] flex items-start gap-2" data-testid="family-helper-banner">
                    <Info className="h-4 w-4 mt-0.5 text-[#1E4F87] shrink-0" />
                    <div>
                      <b>You can add members now</b> or add eligible family members later from your dashboard panel.
                    </div>
                  </div>
                  <p className="text-sm text-[#5C6B7A] mb-4">
                    {plan?.tier === "husband_wife"
                      ? "Husband Wife plan covers you + your spouse."
                      : "Family plan covers Husband + Wife + Children below 18 + Parents (no upper age limit)."}
                    {" "}Add up to {Math.max(0, (plan?.family_members ?? 6) - 1)} additional member{Math.max(0, (plan?.family_members ?? 6) - 1) === 1 ? "" : "s"}. Name, Date of Birth and Mobile are required for each member.
                  </p>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="text-sm text-[#5C6B7A]" data-testid="family-helper-footer">
                      <b className="text-[#0F2540]">{form.family_members.length}</b> of <b>{Math.max(0, (plan?.family_members ?? 6) - 1)}</b> additional members added. Skip this step to add them later from your dashboard.
                    </div>
                    <button type="button" onClick={addFamilyMember}
                      className="rounded-xl bg-[#1E4F87] text-white text-xs font-bold px-3 py-2 hover:bg-[#0F2540] disabled:opacity-50"
                      disabled={form.family_members.length >= Math.max(0, (plan?.family_members ?? 6) - 1)}
                      data-testid="j-add-fam">
                      + Add family member
                    </button>
                  </div>
                  <div className="mt-1 space-y-4">
                    {form.family_members.map((m, i) => {
                      const others = form.family_members.filter((_, idx) => idx !== i);
                      const ageStr = ageLabel(m.dob);
                      return (
                      <div key={i} className="relative rounded-xl border border-[#E1E8F0] p-4 bg-white">
                        <div className="flex items-center justify-between">
                          <div className="text-xs uppercase tracking-wider text-[#A53A2A] font-semibold">Member {i + 2}</div>
                          <button type="button" onClick={() => removeFamilyMember(i)}
                            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md bg-[#E63946]/10 text-[#E63946] hover:bg-[#E63946]/20 font-bold"
                            data-testid={`j-rm-fam-${i}`}>Remove</button>
                        </div>
                        <div className="mt-3 grid sm:grid-cols-2 gap-3">
                          <F label="Full Name" v={m.full_name} s={(v) => updFamilyMember(i, { full_name: v })} required tid={`j-fname-${i}`} />
                          <RelationSelect value={m.relation} onChange={(v) => updFamilyMember(i, { relation: v })} excludeSelf testid={`j-frel-${i}`} required planTier={plan?.tier} otherMembers={others} />
                          <F label="Mobile (optional)" v={m.mobile} s={(v) => updFamilyMember(i, { mobile: v.replace(/\D/g, "").slice(0, 10) })} tid={`j-fmobile-${i}`} />
                          <div>
                            <F label="Date of Birth" type="date" v={m.dob} s={(v) => updFamilyMember(i, { dob: v })} required tid={`j-fdob-${i}`} />
                            {ageStr && <p className="text-[11px] text-[#5C6B7A] mt-1" data-testid={`j-fage-${i}`}>Age · {ageStr}</p>}
                          </div>
                          <div className="sm:col-span-2">
                            <F label="Aadhaar (12-digit)" v={m.aadhaar} s={(v) => updFamilyMember(i, { aadhaar: (v || "").replace(/\D/g, "").slice(0, 12) })} required tid={`j-faadhaar-${i}`} />
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  {form.family_members.length > 0 && (
                    <p className="mt-3 text-xs text-[#5C6B7A]">KYC documents for family members are optional now — you or our staff can upload them later from the member dashboard.</p>
                  )}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6B7A]">Upload your KYC documents (Aadhaar + Photo) — Max 5&nbsp;MB each. JPG / PNG / PDF.</p>
              <div className="rounded-xl bg-[#FFF7E6] border border-[#C89B3C]/30 px-4 py-3 text-xs text-[#0F2540]" data-testid="kyc-optional-banner">
                <strong>Optional now.</strong> You can upload Aadhaar and Photo later from your dashboard panel — your membership card will activate immediately on payment.
              </div>
              <DocUpload label="Aadhaar Card (optional)" folder="aadhaar" testid="upload-aadhaar"
                value={form.documents.aadhaar}
                onChange={(meta) => setForm((f) => ({
                  ...f,
                  documents: { ...f.documents, aadhaar: meta, aadhaar_uploaded: !!meta },
                }))} />
              <DocUpload label="Passport-size Photo (optional)" folder="members" testid="upload-photo" accept="image/*"
                value={form.documents.photo}
                onChange={(meta) => setForm((f) => ({
                  ...f,
                  documents: { ...f.documents, photo: meta, photo_uploaded: !!meta },
                }))} />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6B7A]">Have a discount code from a Nirmal staff member or an existing NPC member? Apply it below.</p>
              <div className="flex gap-2">
                <input type="text" value={form.referral_code} onChange={(e) => { set("referral_code", e.target.value.trim()); setRefStatus(null); }}
                  placeholder="Enter your discount code"
                  className="flex-1 rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                  data-testid="j-referral" />
                <button onClick={verifyReferral} type="button" className="btn-ghost" data-testid="verify-ref-btn">Apply</button>
              </div>
              {refStatus?.valid && (
                <div className="flex items-center gap-2 text-sm text-[#1F7A3D]"><BadgePercent className="h-4 w-4" /> Discount code applied — 5% off your plan at checkout.</div>
              )}
              {refStatus && !refStatus.valid && (
                <div className="text-sm text-[#B91C1C]">Code not found. You can continue without a discount code.</div>
              )}
            </div>
          )}

          {step === 5 && plan && (
            <div className="space-y-3 text-sm">
              <Row k="Plan" v={`${plan.name}`} />
              <Row k="Applicant" v={`${form.full_name} · ${form.mobile}`} />
              <Row k="Email" v={form.email} />
              <Row k="DOB · Gender" v={`${form.dob} · ${form.gender || "—"}`} />
              <Row k="Aadhaar" v={form.aadhaar ? `••••${form.aadhaar.slice(-4)}` : "—"} />
              <Row k="Address" v={form.address} />
              <Row k="State (place of supply)" v={form.state || "—"} />
              <Row k="Family members" v={`${form.family_members_count}${form.family_member_names.length ? " · " + form.family_member_names.filter(Boolean).join(", ") : ""}`} />
              <Row k="Discount code" v={form.referral_code || "—"} />
              <Row k="GST number (B2B)" v={form.gst_number || "—"} />
              {pricing && (
                <div className="mt-4 rounded-2xl border-2 border-[#1E4F87]/20 bg-gradient-to-br from-[#EAF1FA] to-white p-4 space-y-2" data-testid="pricing-breakdown">
                  <div className="flex justify-between" data-testid="pb-plan-price">
                    <span className="text-[#5C6B7A]">Plan price (incl. GST)</span>
                    <span className="font-medium text-[#0F2540]">{inr(plan.price)}</span>
                  </div>
                  <div className="flex justify-between text-xs" data-testid="pb-base-price">
                    <span className="text-[#5C6B7A]">Base price (excl. GST)</span>
                    <span className="text-[#0F2540]">{inr(pricing.base_price)}</span>
                  </div>
                  {pricing.discount > 0 && (
                    <div className="flex justify-between text-xs" data-testid="pb-discount">
                      <span className="text-[#1F7A3D]">
                        Discount ({plan.base_price ? (Math.round((pricing.discount / Number(pricing.base_price)) * 1000) / 10) : 5}% on base)
                      </span>
                      <span className="text-[#1F7A3D]">− {inr(pricing.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs" data-testid="pb-taxable">
                    <span className="text-[#5C6B7A]">Total taxable</span>
                    <span className="text-[#0F2540]">{inr(pricing.taxable)}</span>
                  </div>
                  <div className="flex justify-between text-xs" data-testid="pb-gst">
                    <span className="text-[#5C6B7A]">GST @ {pricing.gst_percent}%</span>
                    <span className="text-[#0F2540]">{inr(pricing.gst)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#1E4F87]/20" data-testid="pb-total">
                    <span className="font-display font-bold text-[#0F2540]">Total payable</span>
                    <span className="font-display text-xl font-bold text-[#A53A2A]">{inr(pricing.final)}</span>
                  </div>
                  {pricing.discount > 0 && (
                    <p className="text-[11px] text-[#1F7A3D] font-medium">You save {inr(plan.price - pricing.final)} on this purchase.</p>
                  )}
                </div>
              )}
              <div className="mt-4 rounded-xl bg-[#EAF1FA] border border-[#1E4F87]/20 p-4 text-[#0F2540] flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1E4F87]" /> By continuing, you agree to our Terms &amp; Privacy.</div>
            </div>
          )}

          {step === 6 && plan && (
            <div className="text-center space-y-4">
              <Lock className="h-10 w-10 text-[#1E4F87] mx-auto" />
              <h3 className="font-display text-2xl font-bold text-[#0F2540]">Pay {inrFull(pricing?.final ?? plan.price)}</h3>
              {pricing && (
                <p className="text-xs text-[#5C6B7A]">
                  {pricing.discount > 0 ? `${inrFull(plan.price)} − ${inrFull(pricing.discount)} referral discount · ` : ""}Price includes {pricing.gst_percent}% GST
                </p>
              )}
              <p className="text-sm text-[#5C6B7A]">Secure Razorpay checkout. Click below to open the secure payment modal.</p>
              <label className="flex items-start gap-3 max-w-md mx-auto text-left text-sm text-[#0F2540] cursor-pointer" data-testid="terms-accept-label">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => set("terms_accepted", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#1E4F87]"
                  data-testid="terms-accept-checkbox"
                />
                <span>
                  I accept the{" "}
                  <Link to="/terms" target="_blank" className="text-[#1E4F87] underline font-semibold" data-testid="terms-link">Terms &amp; Conditions</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" className="text-[#1E4F87] underline font-semibold" data-testid="privacy-link">Privacy Policy</Link>
                  .
                </span>
              </label>
              <button
                onClick={submitPay}
                disabled={loading || !form.terms_accepted}
                className="btn-primary text-base px-8 py-4 mt-2"
                data-testid="join-pay-btn"
              >
                {loading ? "Processing…" : <>Complete Payment <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          )}

          {step < 6 && (
            <div className="mt-8 flex items-center justify-between">
              <button onClick={back} disabled={step === 0} className="btn-ghost disabled:opacity-40" data-testid="join-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="join-next-btn">Next <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}

function F({ label, v, s, type = "text", required, tid, onBlur, err }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0F2540]">{label}{required && " *"}</label>
      <input type={type} value={v} required={required} onChange={(e) => s(e.target.value)} onBlur={onBlur}
        className={`mt-1 w-full rounded-xl border bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87] ${err ? "border-[#B91C1C]" : "border-[#E1E8F0]"}`}
        data-testid={tid} />
      {err && <p className="text-xs text-[#B91C1C] mt-1" data-testid={`${tid}-error`}>{err}</p>}
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#E1E8F0] last:border-none">
      <span className="text-[#5C6B7A]">{k}</span>
      <span className="text-[#0F2540] font-medium text-right">{v}</span>
    </div>
  );
}

function DocUpload({ label, folder = "documents", value, onChange, accept = "image/*,application/pdf", testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setBusy(true);
    try {
      const data_base64 = await new Promise((resolve, reject) => {
        const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
      });
      const res = await api.post("/uploads/document", { filename: file.name, content_type: file.type, data_base64, folder });
      onChange({ doc_id: res.data.doc_id, filename: res.data.filename, size: res.data.size });
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#E1E8F0] p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-[#0F2540]">{label}</div>
          {value ? (
            <div className="text-xs text-[#1F7A3D] mt-0.5">Uploaded · {value.filename}</div>
          ) : (
            <div className="text-xs text-[#5C6B7A] mt-0.5">JPG / PNG / PDF · max 5MB</div>
          )}
        </div>
        <label className="btn-ghost cursor-pointer" data-testid={testid}>
          <input type="file" accept={accept} className="hidden" disabled={busy} onChange={(e) => handle(e.target.files?.[0])} />
          {busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </label>
      </div>
    </div>
  );
}
